/**
 * Coupon Service
 *
 * Type-safe coupon management service using @monorepo/shared schemas
 */

import {
  type CouponQuery,
  type CouponResponse,
  CouponResponseSchema,
  type CouponUsageStats,
  CouponUsageStatsSchema,
  type CreateCoupon,
  CreateCouponSchema,
  type DuplicateCoupon,
  DuplicateCouponSchema,
  type PaginatedCouponsResponse,
  PaginatedCouponsResponseSchema,
  type UpdateCoupon,
  UpdateCouponSchema,
} from '@monorepo/shared/schemas/coupons'
import { del, get, post, put } from '@/lib/api-client'

export const couponService = {
  /**
   * List all coupons (filtered by query)
   */
  async getCoupons(query?: CouponQuery): Promise<PaginatedCouponsResponse> {
    const queryParams = new URLSearchParams()

    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())
    if (query?.search) queryParams.append('search', query.search)
    if (query?.status && query.status !== 'all')
      queryParams.append('status', query.status)
    if (query?.type && query.type !== 'all')
      queryParams.append('type', query.type)
    if (query?.sort_by) queryParams.append('sort_by', query.sort_by)
    if (query?.sort_order) queryParams.append('sort_order', query.sort_order)

    const url = `/coupons${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedCouponsResponseSchema)
  },

  /**
   * Get coupon by ID
   */
  async getCoupon(id: string): Promise<CouponResponse> {
    return get(`/coupons/${id}`, CouponResponseSchema)
  },

  /**
   * Create new coupon
   */
  async createCoupon(data: CreateCoupon): Promise<CouponResponse> {
    const validated = CreateCouponSchema.parse(data)

    return post('/coupons', validated, CouponResponseSchema)
  },

  /**
   * Update coupon
   */
  async updateCoupon(id: string, data: UpdateCoupon): Promise<CouponResponse> {
    const validated = UpdateCouponSchema.parse(data)

    return put(`/coupons/${id}`, validated, CouponResponseSchema)
  },

  /**
   * Delete coupon (soft delete if used, hard delete if not)
   */
  async deleteCoupon(id: string): Promise<void> {
    await del(`/coupons/${id}`)
  },

  /**
   * Duplicate an existing coupon
   */
  async duplicateCoupon(
    id: string,
    data?: DuplicateCoupon
  ): Promise<CouponResponse> {
    const validated = data ? DuplicateCouponSchema.parse(data) : {}

    return post(`/coupons/${id}/duplicate`, validated, CouponResponseSchema)
  },

  /**
   * Get coupon usage statistics
   */
  async getCouponUsageStats(id: string): Promise<CouponUsageStats> {
    return get(`/coupons/${id}/usage-stats`, CouponUsageStatsSchema)
  },
}

export default couponService
